import React from 'react';
import { Avatar, Box, Stack } from '@mui/material';
import { theme } from '../../../../common/styles/theme';
import { DateUtil } from '../../../../common/utils/dateUtil';
import { TimeUtils } from '../../../../common/utils/time-utils';
import { isProgressLogActive } from '../../../../common/utils/conferenceProgress';
import { TConferenceProcessSteps } from '../../../../common/rest/conference/conferenceRest';
import { TAudioType } from '../../../store/models/types';
import { ContentLibVideo } from '../ContentLibVideo/components/ContentLibVideo';
import LangFlag from '../LangFlag/LangFlag';
import { ExercisesProgress } from '../ExercisesProgress/ExercisesProgress';
import { BookChip } from '../BookChip/BookChip';
import { AvatarName } from '../AvatarName/AvatarName';
import { AltInfo, RecordingDescription } from '../RecordingDescription/RecordingDescription';
import { IRecordingTileProps } from './types';

// The backend's placeholder title before AI generation ("9 Jun 2026 15:36", server timezone)
const DEFAULT_TITLE_RE = /^\d{1,2} [A-Z][a-z]{2}\.? \d{4} \d{2}:\d{2}$/;

// One call recording in a list (home list)
export function RecordingTile({
	item,
	me,
	isBig,
	withFlag = true,
	showNative,
	onClick,
	onDelete,
	onDeleteAudio,
	onDeleteVideo,
	onDeleteVideoAndAudio,
	onDeleteForEveryone,
	onDownload,
	onDownloadCaptions,
	onDownloadCaptionsWithTimecodes,
	onCreateBook,
	onOpenBook,
	onOpenExercises,
	onReprocess,
	onProcess,
	onCancel,
	onCloseSettings,
}: IRecordingTileProps) {
	const isCall = item.type === TAudioType.CONFERENCE;
	// unprocessed is explicitly false (not just absent) for a recording that's actually done but has
	// no file by design (saveLevel=transcription/nothing) — only an absent flag means a run is
	// genuinely still live right now.
	const isProcessing = (!isCall && !item.url && item.unprocessed === undefined) || !!item.savingSaveLevel || !!item.deletingMedia || !!item.finishingCall;
	const isLogActive = isProgressLogActive(item.progressLog);
	const noCaptions = item.captionsCount === 0 && !isProcessing && !isLogActive;
	const date = DateUtil.formatDateTime(new Date(item.createdAt));
	// no real title yet: the (local-time) date takes its place, so it isn't repeated below
	const hasTitle = !!item.title && !DEFAULT_TITLE_RE.test(item.title);
	// native mode: the translated AI text, unless that field is the user's own edit; target as a fallback
	const isNativeTitle = !!showNative && !item.hasOwnTitle && !!item.titleNative;
	const isNativeDescription = !!showNative && !item.hasOwnDescription && !!item.descriptionNative;
	const title = isNativeTitle ? item.titleNative : item.title;
	const description = isNativeDescription ? item.descriptionNative : item.description;
	// the other language's version for the tooltips (none for the user's own edit)
	const altTitle = isNativeTitle ? item.title : !item.hasOwnTitle ? item.titleNative : null;
	const altDescription = isNativeDescription ? item.description : !item.hasOwnDescription ? item.descriptionNative : null;
	const hasAlt = (!!altTitle && altTitle !== title) || (!!altDescription && altDescription !== description);
	// thumbnail: the other participants' faces, or mine for a call I was alone in
	const thumbPeople = item.participants?.length ? item.participants : me ? [me] : [];
	//test // const thumbPeople = Array.from({ length: 10 }, (_, i) => ({ ...(item.participants[0] || me!), id: i + 1 }));

	const events = isCall
		? {
			onClick: async () => onClick(item.id),
			onDelete: () => onDelete(item.id),
			onProcess: () => onProcess(item.id),
			onCancel: () => onCancel(item.id),
			onCloseSettings: () => onCloseSettings(item.id),
		}
		: {
			onClick: async () => onClick(item.id),
			onDelete: () => onDelete(item.id),
			onDeleteAudio: () => onDeleteAudio(item.id),
			onDeleteVideo: () => onDeleteVideo(item.id),
			onDeleteVideoAndAudio: () => onDeleteVideoAndAudio(item.id),
			onDeleteForEveryone: () => onDeleteForEveryone(item.id),
			onDownload: () => onDownload(item.id),
			...(noCaptions ? {} : {
				onDownloadCaptions: () => onDownloadCaptions(item.id),
				onDownloadCaptionsWithTimecodes: () => onDownloadCaptionsWithTimecodes(item.id),
				onCreateBook: () => onCreateBook(item.id),
			}),
			...(item.bookId ? { onOpenBook: () => onOpenBook(item.id) } : {}),
			onReprocess: async (force?: boolean, steps?: TConferenceProcessSteps) => onReprocess(item.id, force, steps),
			onCancel: () => onCancel(item.id),
			onCloseSettings: () => onCloseSettings(item.id),
		};

	return (
		<ContentLibVideo
			isBig={isBig}
			isAudio
			isRecording
			isCall={isCall}
			hasAudio={!isCall && !!item.url}
			hasVideo={!isCall && !!item.videoUrl}
			saveLevel={item.saveLevel}
			bookId={item.bookId}
			title={hasTitle ? title! : date}
			modalTitle={date}
			// small tile hides the description, so the title's tooltip carries it
			titleTooltip={!isBig && hasTitle && !!description
				? <Stack
					sx={{
						p: theme.spacing(1),
					}}
				>
					<Box sx={{ fontWeight: 600 }}>{title}</Box>
					<Box sx={{ mt: .5 }}>{description}</Box>
					{hasAlt &&
						<AltInfo>
							{!!altTitle && <Box sx={{ fontWeight: 600 }}>{altTitle}</Box>}
							{!!altDescription && <Box sx={{ mt: .5 }}>{altDescription}</Box>}
						</AltInfo>
					}
				</Stack>
				: undefined
			}
			titlePrefix={withFlag ? <LangFlag code={item.language?.code} /> : undefined}
			recordingThumb={thumbPeople.length === 1 && thumbPeople[0].picture
				// one face: the photo fills the whole thumbnail
				? <Box
					role={'img'}
					aria-label={thumbPeople[0].name}
					sx={{
						position: 'absolute',
						inset: 0,
						backgroundImage: `url("${thumbPeople[0].picture}")`,
						backgroundSize: 'cover',
						backgroundPosition: 'center',
					}}
				/>
				: thumbPeople.length === 1
					? <Avatar
						src={thumbPeople[0].picture}
						alt={thumbPeople[0].name}
						sx={{
							width: 1,
							height: 1,
							fontSize: theme.typography.h6,
						}}
					>
						{thumbPeople[0].name?.charAt(0).toUpperCase()}
					</Avatar>
					// several: the first 4 in a 2×2 grid
					: thumbPeople.length > 1 &&
					<Box
						sx={{
							position: 'absolute',
							inset: 0,
							display: 'grid',
							gridTemplateColumns: '1fr 1fr',
							gridTemplateRows: '1fr 1fr',
							gap: '2px',
						}}
					>
						{thumbPeople.slice(0, 4).map(p => (
							<Stack
								key={p.id}
								role={'img'}
								aria-label={p.name}
								justifyContent={'center'}
								alignItems={'center'}
								sx={{
									minWidth: 0,
									minHeight: 0,
									fontSize: theme.typography.subtitle2,
									fontWeight: 600,
									color: theme.palette.grey[600],
									background: theme.palette.grey[200],
									...(p.picture && {
										backgroundImage: `url("${p.picture}")`,
										backgroundSize: 'cover',
										backgroundPosition: 'center',
									}),
								}}
							>
								{!p.picture && p.name?.charAt(0).toUpperCase()}
							</Stack>
						))}
					</Box>
			}
			thumbUrl={''}
			channel={''}
			info={
				<Stack
					gap={.5}
				>
					{/* description only on the big tile (My library), one line next to the date */}
					<Stack
						direction={'row'}
						alignItems={'baseline'}
						columnGap={1.5}
						sx={{
							minWidth: 0,
						}}
					>
						{hasTitle &&
							<Box
								sx={{
									flexShrink: 0,
									lineHeight: 1.4,
									fontSize: theme.typography.subtitle1,
									color: theme.palette.grey[500],
								}}
							>
								{date}
							</Box>
						}
						{isBig &&
							<RecordingDescription
								text={description}
								altText={altDescription}
								lines={1}
							/>
						}
					</Stack>
					<Stack
						direction={'row'}
						flexWrap={'wrap'}
						alignItems={'center'}
						// the chips carry their own 8px side padding, so a small gap already reads as separate
						columnGap={.5}
						rowGap={.5}
						sx={{
							// ml: '-2px', // avatar centered with flag
							fontSize: theme.typography.subtitle1,
							color: theme.palette.grey[500],
						}}
					>
						{!!item.duration &&
							<Box
								sx={{
									p: theme.spacing(0, .5),
									fontVariantNumeric: 'tabular-nums',
									color: theme.palette.grey[700],
									fontSize: '11px',
									borderRadius: theme.spacing(.5),
									background: theme.palette.grey[100],
								}}
							>
								{TimeUtils.secondsToString(item.duration)}
							</Box>
						}
						<ExercisesProgress
							total={item.exercisesTotal}
							done={item.exercisesDone}
							onOpen={() => onOpenExercises(item.id)}
						/>
						{!!item.bookId &&
							<BookChip onOpen={() => onOpenBook(item.id)} />
						}
						{isBig
							? item.participants?.map(p => (
								<AvatarName
									key={p.id}
									name={p.name}
									picture={p.picture}
									size={2}
								/>
							))
							// small tile: avatars only, names in tooltips
							: !!item.participants?.length &&
								<Stack direction={'row'} gap={.5}>
									{item.participants.map(p => (
										<AvatarName
											key={p.id}
											name={p.name}
											picture={p.picture}
											size={2}
											avatarOnly
										/>
									))}
								</Stack>
						}
					</Stack>
					{noCaptions &&
						<Box
							sx={{
								fontWeight: 500,
								fontSize: theme.typography.subtitle1,
								color: theme.palette.grey[400],
							}}
						>
							No captions
						</Box>
					}
				</Stack>
			}
			duration={item.duration}
			isProcessing={isProcessing}
			audioId={item.id}
			progressLog={item.progressLog}
			startedAt={item.progressStartedAt}
			stepStartedAt={item.progressStepStartedAt}
			updatedAt={item.progressUpdatedAt}
			isCallUnprocessed={item.unprocessed}
			{...events}
		/>
	);
}
